import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import MainLayout from './MainLayout';
import TopNavBar from './TopNavBar';
import { useAuth } from '../context/AuthContext';
import { FiMail, FiClock, FiSettings, FiSave, FiAlertCircle, FiChevronUp, FiChevronDown, FiSearch, FiCheckSquare } from 'react-icons/fi';
import { RiToggleLine, RiToggleFill, RiUserSharedLine } from 'react-icons/ri';
import '../styles/reportsettings.css';
import '../styles/projectmaster.css'; // Reuse PM styles for multiselect

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3030';

/* ─── Multi-Select Dropdown ─── */
function MultiSelect({ options, selected, onChange, placeholder }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = options.filter(o => {
        const label = String(o.label || "").toLowerCase();
        const empId = String(o.empId || "").toLowerCase();
        const searchStr = search.toLowerCase();
        return label.includes(searchStr) || empId.includes(searchStr);
    });

    const toggle = (id) => {
        onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
    };

    const selectedLabels = selected
        .map(id => options.find(o => o.id === id)?.label)
        .filter(Boolean);

    return (
        <div className="pm-multiselect" ref={ref}>
            <div className="pm-multiselect-trigger" onClick={() => setOpen(o => !o)}>
                <div className="pm-multiselect-values">
                    {selectedLabels.length === 0
                        ? <span className="pm-placeholder">{placeholder}</span>
                        : selectedLabels.length <= 2
                            ? selectedLabels.map((l, i) => <span key={i} className="pm-tag">{l}</span>)
                            : <><span className="pm-tag">{selectedLabels[0]}</span><span className="pm-tag-more">+{selectedLabels.length - 1} more</span></>
                    }
                </div>
                {open ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>

            {open && (
                <div className="pm-multiselect-dropdown">
                    <div className="pm-multiselect-search">
                        <FiSearch size={13} />
                        <input
                            autoFocus
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search employees..."
                        />
                    </div>
                    <div className="pm-multiselect-list">
                        {filtered.length === 0
                            ? <div className="pm-multiselect-empty">No results</div>
                            : filtered.map(o => (
                                <label key={o.id} className={`pm-multiselect-item ${selected.includes(o.id) ? "checked" : ""}`}>
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(o.id)}
                                        onChange={() => toggle(o.id)}
                                    />
                                    <div className="pm-emp-info">
                                        <span className="pm-emp-name">{o.label}</span>
                                        <span className="pm-emp-id">ID: {o.empId}</span>
                                    </div>
                                    {selected.includes(o.id) && <FiCheckSquare size={14} className="pm-check-icon" />}
                                </label>
                            ))
                        }
                    </div>
                    {selected.length > 0 && (
                        <div className="pm-multiselect-footer">
                            <button onClick={() => onChange([])}>Clear all</button>
                            <span>{selected.length} selected</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ReportSettings({ isTab = false }) {
    const { user, logout } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [employees, setEmployees] = useState([]);

    const [settings, setSettings] = useState({
        is_enabled: true,
        recipient_emails: '',
        cc_emails: '',
        schedule_time: '09:30'
    });

    useEffect(() => {
        fetchSettings();
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/employee-master?page=1&limit=9999`);
            // The API returns { success: true, data: { employees: [...] } }
            const employeeData = res.data.data?.employees || res.data.employees || [];
            const options = employeeData.map(emp => ({
                id: emp.office_email || emp.email,
                label: emp.full_name,
                empId: emp.employee_id
            })).filter(o => o.id);
            setEmployees(options);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/report-settings`);
            setSettings(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!settings.recipient_emails) {
            return toast.error("Please add at least one recipient email");
        }

        setSaving(true);
        try {
            await axios.post(`${API_BASE_URL}/api/report-settings`, settings);
            toast.success("Report settings updated successfully");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    const content = (
        <div className="report-settings-page">
            {!isTab && (
                <div className="settings-header">
                    <div className="header-info">
                        <h1>Report Notifications</h1>
                        <p>Configure automated attendance reports sent to stakeholders</p>
                    </div>
                </div>
            )}

            <div className="settings-container">
                <div className="settings-card">
                    <div className="card-header">
                        <FiSettings className="card-icon" />
                        <span>Daily Attendance Report Configuration</span>
                    </div>

                    <div className="card-body">
                        {/* Enable/Disable Toggle */}
                        <div className="setting-row toggle-row">
                            <div className="setting-info">
                                <h3>Enable Automated Reports</h3>
                                <p>When enabled, system will send reports based on the schedule below</p>
                            </div>
                            <div
                                className={`toggle-btn ${settings.is_enabled ? 'enabled' : ''}`}
                                onClick={() => setSettings({ ...settings, is_enabled: !settings.is_enabled })}
                            >
                                {settings.is_enabled ? <RiToggleFill size={40} color="#0ea5e9" /> : <RiToggleLine size={40} color="#94a3b8" />}
                            </div>
                        </div>

                        <hr className="divider" />

                        {/* Recipients (TO) */}
                        <div className="setting-row">
                            <div className="setting-info">
                                <div className="label-with-icon">
                                    <FiMail />
                                    <h3>Recipient Emails (TO)</h3>
                                </div>
                                <p>Select employees who will receive the daily report</p>
                            </div>
                            <div className="setting-input">
                                <MultiSelect
                                    options={employees}
                                    selected={settings.recipient_emails ? settings.recipient_emails.split(',').map(s => s.trim()).filter(Boolean) : []}
                                    onChange={(vals) => setSettings({ ...settings, recipient_emails: vals.join(',') })}
                                    placeholder="Select recipients..."
                                />
                            </div>
                        </div>

                        {/* CC Emails */}
                        <div className="setting-row">
                            <div className="setting-info">
                                <div className="label-with-icon">
                                    <RiUserSharedLine />
                                    <h3>CC Emails</h3>
                                </div>
                                <p>Select employees to be kept in CC</p>
                            </div>
                            <div className="setting-input">
                                <MultiSelect
                                    options={employees}
                                    selected={settings.cc_emails ? settings.cc_emails.split(',').map(s => s.trim()).filter(Boolean) : []}
                                    onChange={(vals) => setSettings({ ...settings, cc_emails: vals.join(',') })}
                                    placeholder="Select CC recipients..."
                                />
                            </div>
                        </div>

                        {/* Schedule Time */}
                        <div className="setting-row">
                            <div className="setting-info">
                                <div className="label-with-icon">
                                    <FiClock />
                                    <h3>Schedule Time</h3>
                                </div>
                                <p>At what time should the report for yesterday be sent?</p>
                            </div>
                            <div className="setting-input">
                                <input
                                    type="time"
                                    value={settings.schedule_time}
                                    onChange={(e) => setSettings({ ...settings, schedule_time: e.target.value })}
                                />
                                <span className="timezone-label">Asia/Kolkata (IST)</span>
                            </div>
                        </div>

                        <div className="info-banner">
                            <FiAlertCircle />
                            <span>Report includes yesterday's complete attendance, leave data, and pending checkouts.</span>
                        </div>
                    </div>

                    <div className="card-footer">
                        <button
                            className="btn-save"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : <><FiSave /> Save Configuration</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (isTab) return content;

    return (
        <MainLayout title="Settings" subtitle="Report Settings">
            {content}
        </MainLayout>
    );
}
